/**
 * 파일명 : com0103m.jsx
 * 설명 :  전체게시판 화면
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용      
 ************************************************
 * 1.0       2020-08-10   윤성수     최초작성
 * 1.1       2024-03-12   김혜선     작성일자 조회조건 추가   
 * 1.2       2024-03-12   기라성     엔터키 조회 추가
 * 1.3       2024-03-20   김혜선     작성자콤보로 수정
 */

/************************************************
 * Import 영역  
 ************************************************/
/*  React  */
import React from 'react';

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Com0103mAction } from '../../../redux/slice/com/com0103m_slice';
import { IBoardDcdState, IBoardItemState, IUserItemState } from '../../../redux/models/com/com0103m_model';

/* CSS */

/*  AntD  */
import { Button, Space, Row, Col, Layout, Input, Typography, Collapse, DatePicker, ConfigProvider, Select, Table } from 'antd';
import { SearchOutlined, CaretRightOutlined } from '@ant-design/icons';
import locale from 'antd/lib/locale/ko_KR';

/*  업무  */
import * as common from 'libs/common';
import * as commonProxy from 'libs/CommonProxy';
import dayjs from 'dayjs';

/************************************************
 * 전역변수 영역
 ************************************************/

// moment.locale('ko');
const Com0103m = (props:any) => {
    /**
     * 기능 : State / Reducer 선언
     * param : 
     */
    const dispatch = useAppDispatch();
    const relVal         = useAppSelector((state) => state.Frame.relVal);
    const authBoardItems = useAppSelector((state) => state.Frame.authBoardItems);
    const inquiryCnds    = useAppSelector((state) => state.Com0103m.inquiryCnds);
    const boardList      = useAppSelector((state) => state.Com0103m.boardList);
    const userList       = useAppSelector((state) => state.Com0103m.userList);
    const pageSize       = useAppSelector((state) => state.Com0103m.pageSize);
    const totalCnt       = useAppSelector((state) => state.Com0103m.totalCnt);
    const current        = useAppSelector((state) => state.Com0103m.current);
    const boardDcdList   = useAppSelector((state) => state.Com0103m.boardDcdList);
    const boardDcd       = useAppSelector((state) => state.Com0103m.boardDcd);    

    let tmp = 0;
    const [tableKey, setTableKey] = React.useState<number>(0);
    const [contentHeight, setContentHeight] = React.useState<number>(window.innerHeight);

    /* Render 변수 선언영역 */
    /* AntD */
    const { Content } = Layout;
    const { Text } = Typography;
    const { Panel } = Collapse;
    const { RangePicker } = DatePicker;

    /* 비지니스 */

    /**
     * 기능 : componentDidMount
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     */
     React.useEffect(() => {
        const headerHeight = 335;  // Header 높이
        setContentHeight(window.innerHeight - headerHeight);
        
        const handleResize = () => {
            setContentHeight(window.innerHeight - headerHeight);
            let tmpKey = ++tmp;
            tmp = tmpKey;
            setTableKey(tmpKey);
        };

        /* 카테고리 */
        setBoardDcdList();
        fn_User();

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize); // cleanup
        };
    }, []);

    React.useEffect(() => {
        if (boardDcd) {
            dispatch(Com0103mAction.setInquiryCnds({...inquiryCnds, boardDcd : boardDcd}));
        } else {
            dispatch(Com0103mAction.setInquiryCnds({...inquiryCnds, boardDcd : ''}));
        }
    }, [boardDcdList]);

    const setBoardDcdList = () => {
        const option = {
            useYn: 'Y',
            callback: (datas:IBoardDcdState[]) => {
                let arrOptions:IBoardDcdState[] = [];
                let authArr:any    = [];

                if (!boardDcd) {
                    arrOptions.push({label: '전체', value: ''});
                }

                datas.forEach((item:IBoardDcdState) => {
                    for (let key in authBoardItems) {
                        if (authBoardItems[key].upperMenuId === 'MCOM2021005' && authBoardItems[key].relVal) {
                            authArr.push(authBoardItems[key].relVal);
                        }
                    } 

                    if (authArr.includes(item.cd)) {
                        arrOptions.push({
                            label: item.cdNm,
                            value: item.cd
                        });
                    }
                });

                
                if (boardDcd) {
                    arrOptions = arrOptions.filter((item:IBoardDcdState) => {
                        return item.value === boardDcd;
                    });
                }

                dispatch(Com0103mAction.setBoardDcdList(arrOptions));
            }
        };
        common.getComCodeList('017', option);
    }

    React.useEffect(() => {
        fn_ResetResult();
        fn_Search(1);
    }, [inquiryCnds.boardDcd]);

    /* 투게더콤보 */
    /* 카테고리 */

    /* Table Columns */
    const columns = React.useCallback(() => {
        const arr:any = [
            {
                title: () => <div style={{ textAlign: 'center' }}>번호</div>,
                dataIndex: 'no',
                key: 'no',
                align: 'center',
                width: 50,
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>카테고리</div>,
                dataIndex: 'boardDnm',
                key: 'boardDnm',
                align: 'center',
                width: 160,
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>제목</div>,
                dataIndex: 'title',
                key: 'title',
                align: 'left',
                width: '70%',
                render: (_:string, record:IBoardItemState) => <Button style={{ whiteSpace: 'nowrap' }} className={record.boardChkYn === 'Y' ? 'link-button' : 'link-button-bold'} size='small' onClick={() => showDetail(record)}>{record.title}</Button>
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>작성자</div>,
                dataIndex: 'rgstUserNm',
                key: 'rgstUserNm',
                align: 'center',
                width: 90,
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>작성일시</div>,
                dataIndex: 'rgstDtm',
                key: 'rgstDtm',
                align: 'center',
                width: 160,
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>조회수</div>,
                dataIndex: 'chkCnt',
                key: 'chkCnt',
                align: 'center',
                width: 80,
                render: (_:string, record:IBoardItemState) => record.boardChkCnt +' / ' + record.userCnt
            }];
            return arr;
    }, [boardList]);



    /**
     * 기능 : 초기화 이벤트 선언
     * param : 
     */
    const fn_Reset = () => {
        dispatch(Com0103mAction.initState());
    };

    /**
     * 기능 : 결과초기화 이벤트 선언
     * param : 
     */
    const fn_ResetResult = () => {
        dispatch(Com0103mAction.setBoardList([]));
    };

    /**
     *  기능 : RangePicker 데이터 변경
     *  설명 : RangePicker 데이터 변경값을 받는다
     *  param :
     *  return : None
     */
    const calendar_onChange = (_:any, dateStrings:any) => {
        const startDate = dateStrings[0].split('-').join('');
        const endDate   = dateStrings[1].split('-').join('');

        dispatch(Com0103mAction.setInquiryCnds({...inquiryCnds, inqryStartDt: startDate, inqryEndDt: endDate}));
    };

    /**
     * 기능 : onClick 이벤트 선언
     * param : 
     */
    const onClick = async (e:any, _record:any) => {
        const buttonName = e.currentTarget.name;
        switch (buttonName) {
            case 'btn_Search': {
                fn_ResetResult();
                fn_Search(1);
                break;
            }

            case 'btn_Reset': {
                fn_Reset();
                break;
            }

            default:
        }
    };

    /**
     * 기능 : 상세보기
     * param : 
     */
    const showDetail = (record:IBoardItemState) => {
        const param = {
            menuId : 'MCOM2021008',
            props : {
                id : record.id
            }
        };
        common.openPage(param);
    }

    /**
     * 기능 : 검색조회
     * param : 
     */
    const fn_Search = (page:number) => {
        dispatch(Com0103mAction.setCurrent(page));
        
        /* 변수선언 및 셋팅 영역 */

        // 입력파라미터
        const requestData = {
            ...inquiryCnds,
            pageSize,
            startPage : (page - 1) * pageSize,
            boardDcd : boardDcd ? boardDcd : inquiryCnds.boardDcd
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd : 'TRCOMR0006',
            success : (responseData:any) => {
                dispatch(Com0103mAction.setTotalCnt(responseData.totalCnt));
                dispatch(Com0103mAction.setBoardList(responseData.boardItems));
            },
        });
    };

    React.useEffect(() => {
        fn_Search(current);
    }, [current]);

    /**
     * 기능 : fn_User
     * 설명 : 사용자 콤보박스 데이터 조회
     * param : 
     * return : None
     **/
    const fn_User = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            userStCd: 'A'
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRUSRR0003',
            success: (responseData:any) => {
                // 사용자목록 이름순으로 정렬
                responseData.userList.sort((a:IUserItemState, b:IUserItemState) => (a.userNm > b.userNm) ? 1 : -1)
                dispatch(Com0103mAction.setUserList(responseData.userList));
            },
        });
    };

    const userOptions = React.useCallback(() => {
        const arrUserOptions = [{label: '전체', value: ''}];
        userList.forEach((item:IUserItemState) => {
            arrUserOptions.push({
                label: item.userNm,
                value: item.empNo
            });
        });

        return arrUserOptions;
    }, [userList]);

    /**
     * 기능 : 입력 후 Enter key 입력 시 조회
     * 설명 : 입력컴포넌트
     * param : 
     * return : None
     */
    const handleOnKeyUp = React.useCallback((e:any) => {
        if (e.key === 'Enter') {
            fn_Search(1); // 조회
        }
    },[inquiryCnds.rgstUserNm, inquiryCnds.rgstEmpNo]);

    return (
        <>
            <Layout>
                <Content>
                    <Collapse
                        bordered={false}
                        defaultActiveKey={['1']}
                        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                        className='site-collapse-custom-collapse'
                    >
                        <Panel header='검색조건' key='1' className='site-collapse-custom-panel'>
                            <Row
                                align='middle'
                                justify='space-between'
                                style={{ marginTop: 10, width: '100%' }}
                            >
                                <Col flex='9.5'>
                                    <Space size='large'>
                                        <Space size='small'>
                                            <label>카테코리</label>
                                            <Select
                                                style={{
                                                    width: 200,
                                                }}
                                                optionFilterProp='label'
                                                options={boardDcdList}
                                                onKeyUp={handleOnKeyUp}
                                                onChange={val => {
                                                    dispatch(Com0103mAction.setInquiryCnds({...inquiryCnds, boardDcd: val}))
                                                }}
                                                value={inquiryCnds.boardDcd}
                                            ></Select>
                                        </Space>
                                        <Space size='small'>
                                            <label>작성자</label>
                                            <Select 
                                                showSearch
                                                style={{
                                                    width: 120,
                                                }}
                                                optionFilterProp='label'
                                                defaultValue=''
                                                filterSort={(optionA: any, optionB: any) => {
                                                    const labelA = (optionA?.label ?? '').toLowerCase();
                                                    const labelB = (optionB?.label ?? '').toLowerCase();
                                                  
                                                    if (labelA === '전체') return -1;
                                                    if (labelB === '전체') return 1;
                                                  
                                                    return labelA.localeCompare(labelB);
                                                }}
                                                options={userOptions()}
                                                onKeyUp={handleOnKeyUp}
                                                value={inquiryCnds.rgstEmpNo}
                                                onChange={val => dispatch(Com0103mAction.setInquiryCnds({...inquiryCnds, rgstEmpNo: val}))}
                                            ></Select>
                                        </Space>
                                        <Space size='small'>
                                            <label>제목</label>
                                            <Input
                                                placeholder='입력하세요'
                                                style={{ width: 300 }}
                                                value={inquiryCnds.title}
                                                onChange={(e) => {dispatch(Com0103mAction.setInquiryCnds({...inquiryCnds, title: e.currentTarget.value}))}}
                                                onPressEnter={() => fn_Search(1)}
                                            />
                                        </Space>
                                        <Space>
                                            <label>작성일자</label>
                                            <ConfigProvider locale={locale}>
                                                <RangePicker
                                                    style={{ width: 305 }}
                                                    id='tx_calendar'
                                                    allowClear={false}
                                                    onChange={calendar_onChange}
                                                    value={[inquiryCnds.inqryStartDt ? dayjs(inquiryCnds.inqryStartDt) : null, inquiryCnds.inqryEndDt ? dayjs(inquiryCnds.inqryEndDt) : null]}
                                                />
                                            </ConfigProvider>
                                        </Space>
                                    </Space>
                                </Col>
                                <Col flex='0.5'>
                                    <Space>
                                        <Button
                                            name='btn_Reset'
                                            className='background-color-default-button'
                                            onClick={e => onClick(e, null)}
                                        >
                                            초기화
                                        </Button>
                                        <Button
                                            name='btn_Search'
                                            className='background-color-primary-button'
                                            icon={<SearchOutlined />}
                                            onClick={e => onClick(e, null)}
                                        >
                                            조회
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Panel>
                    </Collapse>
                    <Row align='middle' style={{ height: 50 }}>
                        <Col>
                            <Space>
                                <img className='title-icon' alt='' />
                                <Text key={relVal} strong>{boardDcdList && boardDcdList.length > 0 && boardDcdList[0].value ? boardDcdList[0].label : '전체게시글'}</Text>
                            </Space>
                        </Col>
                    </Row>
                    <Row>
                        <div key={tableKey} style={{ height: contentHeight, display: 'flex', flexDirection: 'column', width : '100%', flex: '1 1 auto', overflowY: 'auto' }}>
                            <Table
                                dataSource={boardList}
                                key='no'
                                columns={columns()}
                                size='small'
                                scroll={{y:contentHeight - 95}}
                                pagination={{
                                    total: totalCnt,
                                    pageSize: pageSize,
                                    current: current,
                                    showTotal : () => '총 ' + totalCnt + ' 건',
                                    onChange: (page, pageSize) => {
                                        dispatch(Com0103mAction.setCurrent(page));
                                        dispatch(Com0103mAction.setPageSize(pageSize));
                                    },
                                }}
                            />
                        </div>
                    </Row>
                </Content>
            </Layout>
        </>
    );
};

export default Com0103m;

